import { useCallback, useMemo } from 'react';

import { useMutation, useQuery } from '@apollo/client';

import { GetPoolSettings, SavePoolSettings } from 'query/__generated__/poolManager';
import { GET_POOL_SETTINGS, SAVE_POOL_SETTINGS } from 'query/poolManager';

export const useUserSettings = (settingName: string) => {
  const savedPoolSettings = useQuery<GetPoolSettings>(GET_POOL_SETTINGS, {
    variables: { settingName },
    fetchPolicy: 'network-only',
  });

  const savedUserSettings = savedPoolSettings.data?.user?.setting;
  // Memoized on the raw string, not recomputed on every render: a consumer
  // (MetricsHeader) depends on this array by reference in a useEffect, and
  // an unmemoized JSON.parse here handed it a new array on every render of
  // whatever calls useUserSettings — including a render triggered by
  // something else entirely (a sibling MetricsHeader's own query settling,
  // a poll, any state change upstream) — not just when the setting
  // actually changed. During an optimistic save (MetricsHeader's isSaving
  // window), that spurious "change" re-ran the effect against the
  // still-stale pre-save value and visibly reverted the just-saved pills
  // until the mutation's own refetch caught up and re-corrected it.
  const userPoolSettings = useMemo(
    () => (savedUserSettings ? JSON.parse(savedUserSettings) : null),
    [savedUserSettings],
  );

  const [addUserSettingMutation] = useMutation<SavePoolSettings>(SAVE_POOL_SETTINGS, {
    onCompleted: () => {
      savedPoolSettings.refetch();
    },
  });

  const addUserSetting = useCallback(
    (value: string) => addUserSettingMutation({ variables: { settingName, value } }),
    [addUserSettingMutation, settingName],
  );

  return {
    addUserSetting,
    savedPoolSettings,
    userPoolSettings,
  };
};
